import { useContext, useEffect, useRef, useState } from "react";
import { useAuth0, IdToken } from "@auth0/auth0-react";
import { useMutation, useQuery } from "react-query";
import { params } from "../../params";
import { useAuthorizedQuery } from "..";
import { FeatureAccessContext } from "../../context/featureAccess";
import {
  Simulator,
  Release,
  ReleaseStatus,
  ReleasePolicyItem,
} from "./simulator.model";
import { ReleaseConversation } from "@/routes/simulator/types";
import { apiResolver, useApi } from "../useApi";

export interface ExtendedRelease extends Omit<Release, "conversations"> {
  conversations: ReleaseConversation[];
}

export function useGetSimulators(onSuccess?: (data: Simulator[]) => void) {
  const { api } = useApi();

  return useQuery({
    queryKey: ["simulators"],
    queryFn: () => apiResolver<Simulator[]>(api.get("/platform/simulators")),
    onSuccess,
  });
}

export function useGetSimulator(simulatorId?: string) {
  const { api } = useApi();

  return useQuery({
    queryKey: ["simulators", simulatorId],
    queryFn: () =>
      apiResolver<Simulator>(api.get(`/platform/simulators/${simulatorId}`)),
    enabled: simulatorId != null,
  });
}

interface GetReleaseResponse extends Omit<Release, "releasePolicy"> {
  releasePolicy: string | ReleasePolicyItem[];
}

// TODO: I'm not proud of this, but it's a workaround to get the release policy back in the correct format
function parseRelease({
  releasePolicy,
  ...restOfData
}: GetReleaseResponse): Omit<Release, "releasePolicy"> & {
  releasePolicy: ReleasePolicyItem[];
} {
  try {
    if (!releasePolicy) {
      return { ...restOfData, releasePolicy: [] };
    }

    if (typeof releasePolicy === "string") {
      const releasePolicyParsed = JSON.parse(releasePolicy);

      return {
        ...restOfData,
        releasePolicy: releasePolicyParsed as ReleasePolicyItem[],
      };
    }

    return {
      ...restOfData,
      releasePolicy: releasePolicy as ReleasePolicyItem[],
    };
  } catch (error) {
    console.error(error);
    throw new Error("Invalid release policy");
  }
}

export function useGetRelease(releaseId?: string) {
  const { api } = useApi();
  return useQuery({
    queryKey: ["release", releaseId],
    queryFn: () =>
      apiResolver<GetReleaseResponse>(
        api.get(`/platform/releases/${releaseId}`)
      ),
    enabled: releaseId != null,
    select: (data) => parseRelease(data),
  });
}

export function useGetSimulatorReleases(
  simulatorId?: string,
  includeConversations?: boolean,
  includeMessages?: boolean
) {
  const queryKey = `simulators_${simulatorId}_releases_${includeConversations}_${includeMessages}`;

  const url = `${params.API_URL}/platform/simulators/${simulatorId}/releases?includeConversations=${includeConversations}&includeMessages=${includeMessages}`;
  const query = useAuthorizedQuery<ExtendedRelease[]>({
    queryKey,
    url: url,
    method: "GET",
    enabled: simulatorId != null,
  });

  return {
    ...query,
  };
}

type UpdateReleaseRequest = {
  id: number;
  simulatorId: number;
  status: ReleaseStatus;
  releasePolicy: string;
};

type UpdateReleaseResponse = Release;

export function useUpdateRelease(releaseId: number) {
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const { selectedWorkspace } = useContext(FeatureAccessContext);

  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  return useMutation<UpdateReleaseResponse, unknown, UpdateReleaseRequest>({
    mutationKey: `update_release_${releaseId}`,
    mutationFn: async (data: UpdateReleaseRequest) => {
      try {
        abortControllerRef.current?.abort();

        const newAbortController = new AbortController();
        abortControllerRef.current = newAbortController;

        const res = await fetch(
          `${params.API_URL}/platform/releases/${releaseId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenId?.__raw ?? ""}`,
              "x-tenant-id": selectedWorkspace ?? "",
            },
            body: JSON.stringify(data),
            signal: newAbortController.signal,
          }
        );

        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          return await res.json();
        } else {
          return await res.text();
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        } else {
          const responseError = {
            type: "Error",
            message:
              (error instanceof Error && error.message) ||
              "Something went wrong",
          };

          const customError = new Error(responseError.message);
          Object.assign(customError, responseError);

          throw customError;
        }
      }
    },
  });
}
